<template>
<div class="container page-frame">
    <Breadcrumbs :here="'配布記録一覧'"
                 :ancestries="ancestries"/>
    <div class="row">
        <div class="col h2"
             :style="{ fontSize: FontSizes.large1}">{{ staff.userName }}さんの配布記録一覧</div>
    </div>
    <div class="row page-tab-row justify-content-md-end">
        <div class="col col-md-2 form-check form-check-inline radio-col">
            <input type="radio"
                   class="form-check-input"
                   :id="tableTabOptions.BY_POSTING_RECORD"
                   :value="tableTabOptions.BY_POSTING_RECORD"
                   autocomplete="off"
                   v-model="selectedTabOption"
                   checked />
            <label class="form-check-label"
                   :for="tableTabOptions.BY_POSTING_RECORD">配布記録ごと</label>
        </div>
        <div class="col col-md-2 form-check form-check-inline radio-col">
            <input type="radio"
                   class="form-check-input"
                   :id="tableTabOptions.BY_DATE"
                   :value="tableTabOptions.BY_DATE"
                   v-model="selectedTabOption"
                   autocomplete="off" />
            <label class="form-check-label"
                   :for="tableTabOptions.BY_DATE">日ごと</label>
        </div>
    </div>
    <div class="row">
        <div class="col table-content-wrap mb-2">
            <template v-if="selectedTabOption === tableTabOptions.BY_POSTING_RECORD">
                <div class="row mb-2 justify-content-between">
                    <div class="col-sm col-md-6 search-text-wrap">
                        <input type="text"
                               placeholder="検索"
                               v-model="searchText">
                        <div class="clear-button"
                             @click="onClickClearSearch"></div>
                    </div>
                    <select class="col-sm col-md-2 diff-state-select" v-model="monthSelectedInt" @change="update">
                        <option v-for="(month, id) in monthList" :key="id" :value="month.monthInt">{{ month.monthString }}</option>
                    </select>
                    <select class="col-sm col-md-3 diff-state-select"
                            v-model="selectedDiffState">
                        <option value="all">すべて</option>
                        <option value="added">追加</option>
                        <option value="changed">変更</option>
                        <option value="deleted">削除</option>
                    </select>
                </div>
                <div class="row">
                    <div class="col diff-table container">
                        <div class="row header-row"
                             :style="{ fontSize: FontSizes.small1}">
                            <div class="col-1"><span class="wrap-text">配布日</span></div>
                            <div class="col-1"><span class="wrap-text">商品ID</span></div>
                            <div class="col-3"><span class="wrap-text">商品名</span></div>
                            <div class="col-1"><span class="wrap-text">配布地区</span></div>
                            <div class="col-2"><span class="wrap-text">配布エリア</span></div>
                            <div class="col-1"><span class="wrap-text">配布数合計</span></div>
                            <div class="col-1"><span class="wrap-text">配布単価</span></div>
                            <div class="col-1"><span class="wrap-text">委託費合計</span></div>
                            <div class="col-1"><span class="wrap-text">変更内容</span></div>
                        </div>
                        <PostingRecordDiffRow v-for="recordDiff in searchedDiffRecords"
                                              :record-diff="recordDiff"
                                              :key="recordDiff.vForKey"/>
                    </div>
                </div>
            </template>
            <template v-else-if="selectedTabOption === tableTabOptions.BY_DATE">
                <div class="row">
                    <div class="col container diff-table">
                        <div class="row header-row"
                             :style="{ fontSize: FontSizes.small1}">
                            <div class="col-3"><span class="wrap-text">配布日</span></div>
                            <div class="col-3"><span class="wrap-text">委託費合計（変更前）</span></div>
                            <div class="col-3"><span class="wrap-text">委託費合計（変更後）</span></div>
                            <div class="col-3"><span class="wrap-text">変更内容</span></div>
                        </div>
                        <DailyDiffRow v-for="dDiff in dailyDiffs"
                                      :key="dDiff.vForKey"
                                      :daily-diff="dDiff"/>
                    </div>
                </div>
            </template>
        </div>
    </div>

</div>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, ref, watch} from "vue";
import {backendApi} from "@/utils/backend-api";
import {useRoute, useRouter} from "vue-router";
import {logoutMessages, subPaths} from "@/utils/consts";
import {amountToString} from "@/utils/string-utils";
import {Staff} from "@/data-models/staff";
import {authStore} from "@/stores/auth-store";
import {authLevels} from "@/data-models/auth";
import {loadingOverlayStateStore} from "@/stores/loading-overlay-state-store";
import {FontSizes} from "@/utils/font-sizes";
import Breadcrumbs, {ancestryType} from "@/components/Breadcrumbs.vue";
import {PaymentAmount} from "@/data-models/payment-amount";
import {isBefore} from "date-fns";
import {PostingRecordDiff} from "@/data-models/posting-record-diff";
import PostingRecordDiffRow from "@/components/PostingRecordDiffRow.vue";
import {DailyDiff} from "@/data-models/daily-diff";
import DailyDiffRow from "@/components/DailyDiffRow.vue";

const tableTabOptions = {
    BY_POSTING_RECORD: "by_posting_record",
    BY_DATE: "by_date"
}


export default defineComponent({
    name: "PostingDataDiffPage",
    components: {DailyDiffRow, PostingRecordDiffRow, Breadcrumbs},
    setup() {
        type MonthType = {
            monthString: string;
            monthInt: number;
        };

        const staffId = ref<number>(-1),
            staff = ref<Staff>(new Staff()),
            ancestries = ref<ancestryType[]>([] as ancestryType[]),
            searchText = ref<string>(""),
            searchedDiffRecords = ref<PostingRecordDiff[]>([] as PostingRecordDiff[]),
            selectedDiffState = ref<string>("all"),
            selectedTabOption = ref<string>(tableTabOptions.BY_POSTING_RECORD),
            monthSelected = ref<MonthType>({} as MonthType),
            monthSelectedInt = ref<number>(1),
            monthList = ref<MonthType[]>([] as MonthType[]),
            dailyDiffs = ref<DailyDiff[]>([] as DailyDiff[])

        let diffRecords = [] as PostingRecordDiff[],
            addedRecords = [] as PaymentAmount[],
            changedRecords = [] as PaymentAmount[],
            deletedRecords  = [] as PaymentAmount[]


        const route = useRoute(),
            router = useRouter()

        const update = async () => {

            const staffResp = await backendApi.loadStaffById(staffId.value)

            if (staffResp.isAuthorized) {
                staff.value = Staff.fromJson(staffResp.value)

                updateAncestries()
                await updateDiffRecords()
                dailyDiffs.value = DailyDiff.fromPostingRecordDiffArrToArr(diffRecords)
                console.log(dailyDiffs.value)
                updateSearchedDiffRecords()

            } else {
                await router.push({
                    path: subPaths.LOGGED_OUT,
                    query: {
                        logout_message: logoutMessages.TOKEN_EXPIRED.key
                    }
                })
            }
        }

        const getLast18Months = () => {
            let months = [];
            let currentDate = new Date();
            currentDate.setMonth(currentDate.getMonth() - 1);

            for (let i = 1; i < 19; i++) {
                let year = currentDate.getFullYear();
                let month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
                const monthObj: MonthType = {
                    monthString: `${year}年${month}月`,
                    monthInt: i
                }
                months.push(monthObj)
                // months.push(`${year}年${month}月`);

                currentDate.setMonth(currentDate.getMonth() - 1);
            }

            monthList.value = months
            monthSelected.value = months[0]
        }

        const updateAncestries = () => {
            ancestries.value = [
                {
                    name: staff.value.displayName + "さん",
                    pathObj: {
                        path: subPaths.MY_PAGE,
                        query: {
                            staff_id: staff.value.staffId,
                        },
                    }
                }
            ]

            if (authStore.getters.authLevel !== authLevels.DIST_STAFF) {
                ancestries.value.unshift({
                    name: staff.value.branch,
                    pathObj: {
                        path: subPaths.BRANCH_PAGE,
                        query: {
                            name: staff.value.branch
                        }
                    }
                })
                ancestries.value.unshift({
                    name: "ホーム",
                    pathObj: subPaths.HOME
                })
            }
        }

        const updateDiffRecords = async () => {
            const diffResp = await backendApi.inquirePostingRecordsDiff(staffId.value, monthSelectedInt.value)
            console.log(diffResp)
            if (diffResp.isAuthorized) {
                diffRecords = PostingRecordDiff.fromJsonArrToArr(diffResp.value.current_posting_record) as PostingRecordDiff[]

                addedRecords = PaymentAmount.fromJsonArrToArr(diffResp.value.added)
                changedRecords = PaymentAmount.fromJsonArrToArr(diffResp.value.changed)
                deletedRecords = PaymentAmount.fromJsonArrToArr(diffResp.value.deleted)

                for (let i = 0; i < diffRecords.length; i++) {
                    const record1 = diffRecords[i]

                    // addedリストに含まれるか
                    const added = addedRecords.find(data => data.id === record1.id)
                    if (added) {
                        record1.diffState = "added"
                    }

                    const changed = changedRecords.find(data => data.id === record1.id)
                    if (changed) {
                        record1.diffState = "changed"
                        record1.oldData = changed
                    }
                }

                deletedRecords.forEach(data => {
                    const record = data as PostingRecordDiff
                    record.diffState = "deleted"
                    diffRecords.push(record)
                })

                // console.log(diffRecords)

            } else {
                await router.push({
                    path: subPaths.LOGGED_OUT,
                    query: {
                        logout_message: logoutMessages.TOKEN_EXPIRED.key
                    }
                })
            }
        }
        
        const updateSearchedDiffRecords = () => {

            let searched1 = [] as PostingRecordDiff[]

            diffRecords.forEach(record => {
                if (searchText.value.length === 0) {
                    // 検索文字列が空白の時は全件表示する
                    searched1.push(record)
                } else {
                    if (record.stringForSearch.includes(searchText.value)) {
                        searched1.push(record)
                    }
                }
            })

            searchedDiffRecords.value.splice(0)

            searched1.forEach(record => {
                if (selectedDiffState.value === "all") {
                    searchedDiffRecords.value.push(record)
                } else {
                    if (selectedDiffState.value === record.diffState) {
                        searchedDiffRecords.value.push(record)
                    }
                }
            })

            searchedDiffRecords.value.sort((a, b) => {
                if (isBefore(b.date as Date, a.date as Date)) {
                    return 1
                } else if (isBefore(a.date as Date, b.date as Date)) {
                    return -1
                } else {
                    if (a.areaId > b.areaId) {
                        return 1
                    } else if (b.areaId > a.areaId) {
                        return -1
                    } else {
                        return a.productId - b.productId
                    }
                }
            })
        }

        watch(() => searchText.value, () => {
            console.log("searchText.value updated")
            updateSearchedDiffRecords()
        })

        watch(() => selectedDiffState.value, () => {
            console.log("selectedDiffState.value updated")
            updateSearchedDiffRecords()
        })

        onMounted(async () => {

            const stId = route.query.staff_id as string
            if (stId) {
                staffId.value = parseInt(stId)
                await update()
            }

            getLast18Months();
            loadingOverlayStateStore.commit("fadeout")
        })

        const onClickClearSearch = () => {
            searchText.value = ""
            updateSearchedDiffRecords()
        }

        watch(() => selectedTabOption.value, () => {
            console.log(selectedTabOption.value)
        })

        return {
            staffId,
            amountToString,
            staff,
            FontSizes,
            ancestries,
            authStore,
            authLevels,
            searchedDiffRecords,
            searchText,
            onClickClearSearch,
            selectedDiffState,
            tableTabOptions,
            selectedTabOption,
            dailyDiffs,
            monthList,
            monthSelectedInt,
            update,
        }
    }
})
</script>

<style scoped lang="scss">

.page-frame {
    height: 100%;
}

.diff-table {
    .header-row {
        border: 2px solid gray;
        background-color: whitesmoke;
        height: 40px;
        //line-height: 36px;
        font-weight: bold;

        div:not(:first-child) {
            border-left: 1px solid gray;
        }

    }
}

.search-text-wrap {
    border: lightgray 1px solid;
    border-radius: 5px;
    height: 40px;

    input {
        border: none;
        outline: none;
        height: 38px;
        width: calc(100% - 40px);
        left: 0;
        position: absolute;
        text-indent: 5px;
        border-radius: 5px;
    }

    .clear-button {
        position: absolute;
        right: 0;
        top: 0;
        width: 40px;
        height: 40px;
        border-radius: 5px;

        &:hover {
            opacity: 0.7;
        }

        &:active {
            opacity: 0.5;
        }

        &::before {
            content: "";
            width: 30px;
            height: 4px;
            border-radius: 2px;
            background-color: lightgray;
            position: absolute;
            transform: rotate(45deg);
            top: 16px;
            left: 6px
        }

        &::after {
            content: "";
            width: 30px;
            height: 4px;
            border-radius: 2px;
            background-color: lightgray;
            position: absolute;
            transform: rotate(-45deg);
            top: 16px;
            left: 6px
        }
    }
}

.diff-state-select {
    height: 40px;
}

.page-tab-row {
    height: 40px;
    .radio-col {
        //background-color: red;
    }
}


</style>